import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { analyzeTextForPersona } from '../../services/apiService';
import { parseApiError } from '../../utils/errorUtils';
import LoadingOverlay from '../atoms/LoadingOverlay';
import ImageUpload from './ImageUpload';
import styles from './Initializer.module.css';

const MIN_LENGTH = 100;
const MAX_LENGTH = 10000;

const modeDescriptions = {
  'historical-figure':
    'Uses its knowledge to fill in known historical facts about the person.',
  'fictional-character':
    "Uses its knowledge to fill in facts from the character's known story or universe.",
  'expert-role':
    'Uses its knowledge of the specified profession to add relevant context and terminology.',
  'digital-me':
    'Enriches the persona by inferring deeper psychological traits from the provided text.',
  'creative-artist':
    'Takes creative license to expand on the artistic themes and ideas in the text.',
  'brand-mascot':
    'Enriches the persona with common branding and positive communication principles.',
  'ai-assistant':
    "Uses the AI's general knowledge to expand its capabilities as a helpful assistant.",
  'abstract-concept':
    "Uses philosophical and metaphorical knowledge to deepen the concept's embodiment.",
  default:
    "Uses the AI's general knowledge to add relevant context and details.",
};

const loadingMessages = [
  'Analyzing text...',
  'Inferring psychological traits...',
  'Calibrating personality matrix...',
  'Defining communication style...',
  'Synthesizing narrative core...',
  'Building persona...',
  'Almost done!...',
];

function CreateFromTextStep({
  personaData,
  onUploadSuccess,
  onAnalysisComplete,
  onBack,
  currentStepNumber,
  totalSteps,
}) {
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [analysisMode, setAnalysisMode] = useState('enriched');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { getAccessTokenSilently } = useAuth0();
  const [finalImageUrl, setFinalImageUrl] = useState('');

  const handleUpload = ({ finalImageUrl, previewUrl }) => {
    setFinalImageUrl(finalImageUrl);
    onUploadSuccess({ finalImageUrl, previewUrl });
  };

  const handleAnalyze = async () => {
    if (!name.trim()) {
      setError('Persona name is required.');
      return;
    }
    if (text.length < MIN_LENGTH || text.length > MAX_LENGTH) {
      setError(
        `Text must be between ${MIN_LENGTH} and ${MAX_LENGTH} characters.`
      );
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const accessToken = await getAccessTokenSilently();
      const payload = {
        text_to_analyze: text,
        personaName: name,
        type: personaData.type,
        analysisMode: analysisMode,
      };
      const generatedPersonaData = await analyzeTextForPersona(
        payload,
        accessToken
      );

      const finalData = {
        ...generatedPersonaData,
        imageUrl: finalImageUrl,
      };

      onAnalysisComplete(finalData);
    } catch (e) {
      const formattedError = parseApiError(e);
      setError(formattedError);
      setIsLoading(false);
    }
  };

  const enrichedDescription =
    modeDescriptions[personaData.type] || modeDescriptions['default'];

  return (
    <div>
      <LoadingOverlay isLoading={isLoading} messages={loadingMessages} />
      <h3>
        Step {currentStepNumber}/{totalSteps}: Provide Name & Text
      </h3>

      <div className={styles.formGroup}>
        <label>Persona Avatar</label>
        <ImageUpload onUploadSuccess={handleUpload} />
      </div>

      <p className={styles.helperText}>
        Give your persona a name, then paste a block of text for the AI to
        analyze.
      </p>

      <div className={styles.formGroup}>
        <label htmlFor="personaName">Persona Name</label>
        <input
          type="text"
          id="personaName"
          name="personaName"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Jeeves"
          disabled={isLoading}
        />
      </div>

      <div className={styles.formGroup}>
        <div className={styles.labelRow}>
          <label htmlFor="text-analysis">Text for Analysis</label>
          <span className={styles.charCounter}>
            {text.length} / {MAX_LENGTH}
          </span>
        </div>
        <textarea
          id="text-analysis"
          name="text-analysis"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows="15"
          placeholder="Paste your character description, journal entry, or 'about me' text here..."
          disabled={isLoading}
        />
      </div>

      <fieldset className={styles.configGroup}>
        <legend>Analysis Mode</legend>
        <div className={styles.radioGroup}>
          <label>
            <input
              type="radio"
              className={styles.radioInput}
              name="analysisMode"
              value="strict"
              checked={analysisMode === 'strict'}
              onChange={(e) => setAnalysisMode(e.target.value)}
            />
            <span className={styles.radioControl}></span>
            <span className={styles.radioText}>
              <strong>Strict:</strong> Analyze only the text you provide.
            </span>
          </label>
          <label>
            <input
              type="radio"
              className={styles.radioInput}
              name="analysisMode"
              value="enriched"
              checked={analysisMode === 'enriched'}
              onChange={(e) => setAnalysisMode(e.target.value)}
            />
            <span className={styles.radioControl}></span>
            <span className={styles.radioText}>
              <strong>Enriched (Recommended):</strong> {enrichedDescription}
            </span>
          </label>
          <label>
            <input
              type="radio"
              className={styles.radioInput}
              name="analysisMode"
              value="creative"
              checked={analysisMode === 'creative'}
              onChange={(e) => setAnalysisMode(e.target.value)}
            />
            <span className={styles.radioControl}></span>
            <span className={styles.radioText}>
              <strong>Creative:</strong> Allow the AI to invent plausible
              details.
            </span>
          </label>
        </div>
      </fieldset>

      {error && <p className={styles.saveError}>{error}</p>}

      <div className={styles.buttonGroup}>
        <button
          onClick={onBack}
          className="action-button secondary"
          disabled={isLoading}
        >
          Back to Type
        </button>
        <button
          onClick={handleAnalyze}
          className="action-button primary"
          disabled={isLoading}
        >
          {isLoading ? 'Analyzing...' : 'Analyze & Create'}
        </button>
      </div>
    </div>
  );
}

export default CreateFromTextStep;
